import { GeckoBinnedDataBuffer, useCdfRawValues } from 'features/dataLayers'
import { binToMeasurement } from 'features/fde'
import { useIndicationData } from 'features/indicationTriage/IndicationContext'
import { useRolodexEntries } from 'features/rolodex'
import { useMemo } from 'react'
import {
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoMeasurement,
  GeckoMeasurementUnit,
  RolodexEntryType,
} from 'types'

const ROLODEX_TYPE_CRITICAL_THICKNESS_PROFILE =
  RolodexEntryType.FFS_CRITICAL_THICKNESS_PROFILE

export type CriticalProfilePoint = {
  coords: GeckoComponentCoords
  value: GeckoMeasurement | undefined
}

export interface CriticalProfile {
  id: string
  name: string
  points: ReadonlyArray<CriticalProfilePoint>
  cdf_id: string | undefined
  index_axis: 'x' | 'y' | undefined
}

export type CriticalProfiles = {
  profiles: CriticalProfile[]
  cdf: GeckoComponentDataFrame | undefined
}

export function useGetCriticalProfiles(indicationId: string | undefined): {
  data: CriticalProfiles
  isLoading: boolean
  forceRefresh: () => void
} {
  const rolodexSwr = useRolodexEntries(
    useMemo(
      () =>
        indicationId
          ? [
              {
                type: ROLODEX_TYPE_CRITICAL_THICKNESS_PROFILE,
                tags: {
                  indication: indicationId,
                },
              },
            ]
          : [],
      [indicationId]
    )
  )
  const { relevantThicknessCDF: cdf } = useIndicationData()
  const { buffer: binnedDataBuffer, isLoading: cdfDataLoading } =
    useCdfRawValues(cdf)

  return useMemo(
    () => ({
      data: {
        profiles:
          rolodexSwr.entries?.map((entry) => {
            return {
              id: entry.id,
              points: (entry.data.critical_path_bins as [number, number][])
                .map((bin) => binToValue(cdf, binnedDataBuffer, bin))
                .filter(
                  (profilePoint) =>
                    !!profilePoint && profilePoint.value !== undefined
                ) as CriticalProfilePoint[],
              cdf_id: entry.tags['component-data-frame'] ?? undefined,
              index_axis: (entry.data['index_axis'] ?? undefined) as
                | 'x'
                | 'y'
                | undefined,
              name: `Profile ${entry.id.substring(0, 4)}`,
            }
          }) ?? [],
        cdf,
      },
      isLoading: rolodexSwr.isLoading || cdfDataLoading,
      forceRefresh: rolodexSwr.forceRefresh,
    }),
    // Le smell: The card draws markup based on this, which
    // invalidates the entries, which means we re-draw the markup,
    // which invalidates the entries and so on. This ugly fix breaks
    // that cycle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      cdf,
      binnedDataBuffer,
      cdfDataLoading,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      rolodexSwr.entries.map((e) => e.id).join('~'),
      rolodexSwr.forceRefresh,
      rolodexSwr.isLoading,
    ]
  )
}

const STD_UNIT = GeckoMeasurementUnit.Millimeters

function binToValue(
  cdf: GeckoComponentDataFrame | undefined,
  data: GeckoBinnedDataBuffer | undefined,
  bin: [number, number]
): CriticalProfilePoint | undefined {
  if (!cdf) {
    return undefined
  }
  const [binX, binY] = bin
  const numXBins = cdf.data.binning.x_bin_count
  const dataIdx = binY * numXBins + binX
  const rawValue =
    data && dataIdx <= data.values.length
      ? (data.values.at(dataIdx) ?? NaN)
      : NaN
  const value = isNaN(rawValue)
    ? undefined
    : GeckoMeasurement.CreateFromValue(
        rawValue,
        cdf.data.params.measurement_unit
      )

  const binWidth = cdf.data.binning.bin_size.width.convertTo(STD_UNIT).num
  const binHeight = cdf.data.binning.bin_size.height.convertTo(STD_UNIT).num
  const geomStartX = cdf.data.geometry.start.x.convertTo(STD_UNIT).num
  const geomStartY = cdf.data.geometry.start.y.convertTo(STD_UNIT).num
  const coordX = binToMeasurement(binX, geomStartX, binWidth)
  const coordY = binToMeasurement(binY, geomStartY, binHeight)
  const coords = GeckoComponentCoords.CreateFromValues(
    cdf.data.geometry.componentID,
    coordX,
    STD_UNIT,
    coordY,
    STD_UNIT
  )
  return { coords, value }
}
